<template>
    <portal>
        <v-row>
            <v-col
                v-if="clientPortalSetting.assets_liabilities_page"
                md="3"
                sm="12">
                <accounts-overview
                    :banking="bankAccounts"
                    :tax-qualifed="taxQualified"
                    :non-tax-qualifed="nonQualifiedAccounts"
                    :properties="properties"
                    :insurances="lifeInsurances"
                    :liabilities="liabilities"/>
            </v-col>
            <v-col
                :md="clientPortalSetting.assets_liabilities_page ? 9 : 12"
                sm="12">
                <v-row>
                    <v-col sm="12">
                        <market-index-ticker-container :indexes="marketIndexes"/>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col sm="12">
                        <year-to-date-preview
                            v-if="integrationStatus.performance_reporting_active"
                            :performance="localPerformance"/>
                        <brokerage-accounts
                            v-else
                            :show-performance="false"></brokerage-accounts>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col sm="6">
                        <span class="font-weight-bold" style="font-size: 25px;">NET WORTH:</span>
                    </v-col>
                    <v-col sm="6">
                        <span class="font-weight-bold blue--text" style="font-size: 25px; float: right">{{ totals['totalNetworth'] | money }}</span>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        md="6"
                        sm="12">
                        <donut-chart :data="assetsData" barLabel="Assets" />
                    </v-col>
                    <v-col
                        md="6"
                        sm="12"
                        class="text-right">
                        <donut-chart :data="liabilitiesData" barLabel="Liabilities" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col sm="12">
                        <year-to-date-high-charts
                            v-if="performance"
                            :performance="performance"
                            style="margin-bottom: 0px !important;"/>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

    </portal>

</template>

<script>
import Portal from "@/components/Portal";
import MarketIndexTickerContainer from "@/components/dashboard/MarketIndexTickerContainer";
import YearToDatePreview from "@/components/dashboard/YearToDatePreview";
import {mapState, mapActions, mapMutations, mapGetters} from "vuex";
import DonutChart from "@/components/dashboard/DonutChart";
import AccountsOverview from "@/components/dashboard/AccountsOverview";
import BrokerageAccounts from "@/components/accounts/BrokerageAccounts";
import YearToDateHighCharts from "@/components/year-to-date/YearToDateHighCharts.vue";

export default {
    components: {
        YearToDateHighCharts,
        AccountsOverview,
        DonutChart,
        BrokerageAccounts,
        YearToDatePreview,
        MarketIndexTickerContainer,
        Portal
    },

    computed: {
        ...mapState('auth', [
            'user',
            'clientPortalSetting'
        ]),
        ...mapState('netWorthSummary', [
            'totals'
        ]),

        ...mapState('household', [
            'performance',
            'integrationStatus'
        ]),
        ...mapState('marketIndexes', [
            'marketIndexes'
        ]),
        ...mapGetters('accounts', [
            'taxQualified',
            'nonQualifiedAccounts',
            'bankAccounts'
        ]),
        ...mapGetters('liability', ['liabilities']),
        ...mapGetters('property', ['properties']),
        ...mapGetters('lifeInsurance', ['lifeInsurances']),
        assetsData() {
            return [
                {
                    type: 'Banking',
                    items: this.bankAccounts,
                    color: '#887700'
                },
                {
                    type: 'Investment',
                    items: this.nonQualifiedAccounts,
                    color: '#bcbcbc'
                },
                {
                    type: 'Retirement',
                    items: this.taxQualified,
                    color: '#2986cc'
                },
                {
                    type: 'Insurance',
                    items: this.lifeInsurances,
                    color: '#e69138'
                },
                {
                    type: 'Other Assets',
                    items: this.assets,
                    color: '#118800'
                }
            ]
        },
        liabilitiesData() {
            return [
                {
                    type: 'Credit Card and Others',
                    items: this.liabilities,
                    color: '#887700'
                },
                {
                    type: 'Mortgage',
                    items: this.properties,
                    color: '#bcbcbc'
                }
            ]
        }
    },

    data() {
        return {
            localPerformance: null,
            totalValue: 0.00,
            assetDonutChartData: [
                {
                    name: 'Banking',
                    value: 10000,
                    color: '#887700'
                },
                {
                    name: 'Investment',
                    value: 40000,
                    color: '#bcbcbc'
                },
                {
                    name: 'Retirement',
                    value: 80000,
                    color: '#2986cc'
                },
                {
                    name: 'Insurance',
                    value: 80000,
                    color: '#e69138'
                }
            ]
        }
    },

    mounted() {
        this.getTotalManagedAccount();
        this.summary({householdId: this.user.household_id});
        this.fetchHousehold(this.user.household_id);

        this.$store.dispatch('household/fetchIntegrationStatus').then(() => {
            if (this.integrationStatus.performance_reporting_active) {
                this.fetchPerformance(true);
            }
        });


        this.fetchIncomePlannerVariables(this.user.household_id);
        this.fetchMarketIndexes();

        window.Echo.channel('client.report.twelveMonthsPerformance.' + this.user.household_id)
            .listen('.Household\\Events\\ClientReportCompleteEvent', (e) => {
                this['PERFORMANCE_SET'](e.performance);
            });
        this.$nextTick(() => {
            //Need to wait until all neighbour components render in two column displays,  otherwise highcharts is out of bounds
            this.localPerformance = this.performance;
        });
    },

    created() {
        this.fetchHouseholdAccountData(this.user.household_id);
        this.fetchIntegrationInformation({householdId: this.user.household_id});
        this.loadBeneficiaries();
        this.loadIndividuals();
        this.loadAccounts();
    },

    methods: {
        ...mapActions('household', [
            'fetchPerformance',
            'fetchHousehold',
            'fetchHouseholdAccountData'
        ]),
        ...mapMutations('household', [
            'PERFORMANCE_SET'
        ]),
        ...mapActions('netWorthSummary', [
            'summary',
        ]),
        ...mapActions('incomePlanner', [
            'fetchIncomePlannerVariables',
        ]),
        ...mapActions('accounts', [
            'getTotalManagedAccount',
            'loadAccounts'
        ]),
        ...mapActions('marketIndexes', [
            'fetchMarketIndexes'
        ]),
        ...mapActions('lifeInsurance', [
            'loadLifeInsurances'
        ]),
        ...mapActions('asset', [
            'loadAssets'
        ]),
        ...mapActions('property', [
            'loadProperties'
        ]),
        ...mapActions('liability', [
            'loadLiabilities'
        ]),
        ...mapActions('individuals', [
            'loadIndividuals',
        ]),
        ...mapActions('beneficiaries', [
            'loadBeneficiaries',
        ]),
        ...mapActions('aqumulate', [
            'fetchIntegrationInformation',
        ])
    },

    watch: {
        performance() {
            this.localPerformance = this.performance;
        }
    }
}
</script>
